import { useEffect, useState, type ReactNode } from "react";
import { postJson } from "../api";
import { useAiData, useTitle } from "../ai/parts";
import {
  Counter,
  DataTable,
  FilterRow,
  MoreFilters,
  MobileActions,
  NoResults,
  Pill,
  PillDivider,
  RowMenu,
  RowsSkeleton,
  SearchBox,
  useFilters,
  useIsMobile,
  Window,
} from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { OrgNotFound } from "../pages/StatePages";
import { useOpenOrg } from "../shell/openOrg";
import { formatText, useTexts } from "../texts";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { ChatIcon } from "../ui/icons/Icon";
import { Notice, PageHead, TextField, useToast } from "../ui/kit";
import { ListFooter, useServerList } from "../ui/list";
import { PageState, PasswordField } from "../ui/pieces";
import { optionsNote, useServerOptions } from "../ui/serverOptions";
import {
  dateIn,
  initials,
  joinList,
  moduleName,
  peopleListKey,
  personHue,
  plural,
  roleKey,
  statusKey,
  userHref,
  whenIn,
  type MemberRow,
} from "./organizations";
import { ConfirmWindow, usePersonActions } from "./PersonActions";
import styles from "./orgs.module.css";

type OrgHead = { id: number; name: string; code: string; timezone: string };
type Filters = {
  modules: { code: string; name: string }[] | null;
  inboxes: { inboxId: number; name: string }[] | null;
};

/** Encabezado «Personas» con su acción y las pestañas Usuarios · Invitaciones · Propietario (C03). */
function PeopleHead({
  orgId,
  current,
  action,
}: {
  orgId: number;
  current: "users" | "invites" | "owner";
  action: ReactNode;
}) {
  const { L } = useTexts();
  const tabs = [
    { key: "users", label: L("ADM_PPL_TAB_USERS"), href: `/Organizations/Usuarios?id=${orgId}` },
    { key: "invites", label: L("ADM_PPL_TAB_INVITES"), href: `/Organizations/Invitaciones?id=${orgId}` },
    { key: "owner", label: L("ADM_PPL_TAB_OWNER"), href: `/Organizations/Propietario?id=${orgId}` },
  ];
  return (
    <>
      <PageHead title={L("ADM_ORG_PEOPLE")}>{action}</PageHead>
      <nav className={`tm-tabs ${ui.tabs}`} aria-label={L("ADM_ORG_PEOPLE")} data-part="pestanas">
        {tabs.map((tab) => (
          <a key={tab.key} href={tab.href} aria-current={tab.key === current ? "page" : undefined}>
            {tab.label}
          </a>
        ))}
      </nav>
    </>
  );
}

/** «Agregar usuario»: botón en escritorio, «+» redondo en el celular. */
function AddUser({ orgId }: { orgId: number }) {
  const { L } = useTexts();
  const href = `/Organizations/AgregarUsuario?id=${orgId}`;
  return (
    <>
      <a className={`tm-btn primary ${ui.desktopInline}`} href={href}>
        <ChatIcon name="plus" />
        {L("ORG_ADD_USER")}
      </a>
      <a className={ui.fab} href={href} aria-label={L("ORG_ADD_USER")} title={L("ORG_ADD_USER")}>
        <ChatIcon name="plus" />
      </a>
    </>
  );
}

/** Iniciales de una persona en su círculo (C17), con su tono (C20, AR23): por su id o, sin id, por su nombre. */
export function Initials({ name, large, seed }: { name: string; large?: boolean; seed?: number }) {
  return (
    <span
      className={`${styles.initials} npa-person-tone${large ? ` ${styles.initialsLarge}` : ""}`}
      style={{ ["--person-hue" as string]: String(personHue(seed ?? name)) }}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}

/** Estado de una persona: Activo e Inactivo en texto, Bloqueado en ocre (C15). */
export function PersonStatusTag({ status }: { status: string }) {
  const { L } = useTexts();
  return status === "blocked" ? (
    <span className="tm-tag ochre">{L(statusKey(status))}</span>
  ) : (
    <>{L(statusKey(status))}</>
  );
}

/** Rol: Propietario en su etiqueta de atención; Administrador y Miembro en texto. */
export function RoleTag({ role }: { role: string }) {
  const { L } = useTexts();
  return role === "ORG_OWNER" ? <span className="tm-tag accent">{L(roleKey(role))}</span> : <>{L(roleKey(role))}</>;
}

const statuses = ["", "active", "blocked", "inactive"] as const;
const statusPill: Record<string, string> = {
  "": "ADM_PPL_FILTER_ALL",
  active: "ADM_PPL_FILTER_ACTIVE",
  blocked: "ADM_PPL_FILTER_BLOCKED",
  inactive: "ADM_PPL_FILTER_INACTIVE",
};

/**
 * `emp-usuarios` · Personas › Usuarios (§5.4): buscador, píldoras de estado y «Más filtros» (Rol, Módulo, Bandeja), todo
 * en la dirección; tabla C28 paginada en Identity (B3) con módulos y bandejas en la misma respuesta. La fila abre la ficha
 * (una persona bloqueada, en Seguridad); el ⋮ tiene Desbloquear, Restablecer contraseña, Cerrar sesiones y Desactivar.
 */
export function UsersPage({ orgId }: { orgId: number }) {
  const { L, locale } = useTexts();
  const { params, get, set } = useFilters();
  const mobile = useIsMobile();
  const search = get("search");
  const status = get("status");
  const role = get("role");
  const module = get("module");
  const inbox = get("inbox");
  const query = new URLSearchParams(
    Object.entries({ search, status, role, module, inbox }).filter(([, value]) => value) as [string, string][],
  ).toString();
  const list = useServerList<MemberRow>(`/api/admin/organizations/${orgId}/members${query ? `?${query}` : ""}`);
  const org = (list.extra?.organization as OrgHead | undefined) ?? null;
  const filters = (list.extra?.filters as Filters | undefined) ?? null;
  const inboxesAvailable = (list.extra?.inboxesAvailable as boolean | undefined) ?? true;
  useOpenOrg(org);
  useTitle(L("ADM_ORG_PEOPLE"));
  const actions = usePersonActions(orgId, org?.name ?? "", list.reload);
  // ← de la ficha vuelve a esta lista con su filtro y su página (comodidad de esta pestaña; nunca decide nada).
  const listSearch = params.toString();
  useEffect(() => {
    try {
      sessionStorage.setItem(peopleListKey(orgId), listSearch);
    } catch {
      // Sin almacenamiento, ← vuelve a la lista sin filtro.
    }
  }, [orgId, listSearch]);

  if (list.state === "notFound") return <OrgNotFound />;
  const head = <PeopleHead orgId={orgId} current="users" action={<AddUser orgId={orgId} />} />;
  if (list.state === "forbidden")
    return <PageState head={head} title={L("ADM_FORBIDDEN")} line={L("ADM_FORBIDDEN_LINE")} />;

  const moduleNames = Object.fromEntries((filters?.modules ?? []).map((item) => [item.code, item.name]));
  const inboxName = (filters?.inboxes ?? []).find((item) => String(item.inboxId) === inbox)?.name;
  const roleOptions = ["ORG_OWNER", "ORG_ADMIN", "ORG_MEMBER"].map((value) => ({ value, label: L(roleKey(value)) }));
  const applied = [
    role ? { key: "role", label: L(roleKey(role)) } : null,
    module ? { key: "module", label: moduleName(L, module, moduleNames) } : null,
    inbox ? { key: "inbox", label: inboxName ?? L("ADM_PPL_INBOX") } : null,
  ].filter(Boolean) as { key: string; label: string }[];
  const filtered = !!(search || status || role || module || inbox);

  return (
    <>
      {head}
      <div className={ui.toolbar}>
        <SearchBox
          id="people-search"
          value={search}
          placeholder={L("ADM_PPL_SEARCH")}
          onChange={(value) => set({ search: value })}
        />
        <FilterRow label={L("ADM_AI_FILTERS")}>
          {statuses.map((value) => (
            <Pill key={value || "all"} pressed={status === value} onClick={() => set({ status: value })}>
              {L(statusPill[value]!)}
            </Pill>
          ))}
          {applied.map((item) => (
            <Pill
              key={item.key}
              pressed
              onClick={() => set({ [item.key]: "" })}
              label={`${L("ADM_AI_REMOVE_FILTER")}: ${item.label}`}
            >
              {item.label}
              <ChatIcon name="x" />
            </Pill>
          ))}
          <PillDivider />
          <MoreFilters
            groups={[
              { key: "role", title: L("ADM_PPL_ROLE"), options: roleOptions },
              {
                key: "module",
                title: L("ADM_PPL_MODULE"),
                options: (filters?.modules ?? []).map((item) => ({
                  value: item.code,
                  label: moduleName(L, item.code, moduleNames),
                })),
              },
              {
                key: "inbox",
                title: L("ADM_PPL_INBOX"),
                options: (filters?.inboxes ?? []).map((item) => ({ value: String(item.inboxId), label: item.name })),
              },
            ]}
            value={{ role, module, inbox }}
            onApply={(next) => set({ role: next.role ?? "", module: next.module ?? "", inbox: next.inbox ?? "" })}
          />
        </FilterRow>
      </div>
      {!inboxesAvailable ? <Notice tone="warning">{L("ADM_PPL_INBOXES_UNAVAILABLE")}</Notice> : null}
      {list.state === "loading" ? (
        <RowsSkeleton rows={6} table={!mobile} />
      ) : list.state === "error" ? (
        <Notice
          tone="error"
          action={
            <button type="button" className="tm-btn secondary" onClick={list.reload}>
              {L("ADM_RETRY")}
            </button>
          }
        >
          {L("ADM_UNAVAILABLE_LINE")}
        </Notice>
      ) : list.items.length === 0 ? (
        filtered ? (
          <NoResults
            query={search}
            things={L("ADM_PPL_THINGS")}
            onClear={() => set(search ? { search: "" } : { status: "", role: "", module: "", inbox: "" })}
          />
        ) : (
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_PPL_EMPTY")}</strong>
            <a className="tm-btn primary" href={`/Organizations/AgregarUsuario?id=${orgId}`}>
              {L("ORG_ADD_USER")}
            </a>
          </div>
        )
      ) : (
        <>
          <Counter>{plural(L, locale, list.total, "ADM_N_USERS_ONE", "ADM_N_USERS")}</Counter>
          {mobile ? (
            // C17 · iniciales, nombre con su etiqueta y una línea con correo, estado, rol y último acceso (§5.4).
            <ul className={`tm-list ${ui.list}`} aria-label={L("ADM_PPL_TAB_USERS")} data-part="lista">
              {list.items.map((row) => (
                <li key={row.userId} className={ui.row}>
                  <a className={ui.rowLinkArea} href={userHref(orgId, row.userId, row.status)}>
                    <Initials name={row.name} seed={row.userId} large />
                    <span className={ui.rowText}>
                      <span className={ui.line1}>
                        <strong>{row.name}</strong>
                        {row.isOwner ? (
                          <span className="tm-tag accent">{L("ADM_PPL_ROLE_OWNER")}</span>
                        ) : row.status === "blocked" ? (
                          <span className="tm-tag ochre">{L(statusKey(row.status))}</span>
                        ) : null}
                      </span>
                      <span className={`${ui.line2} ${styles.oneLine}`}>
                        {[
                          row.email,
                          L(statusKey(row.status)),
                          L(roleKey(row.role)),
                          row.lastLoginAt
                            ? whenIn(L, locale, row.lastLoginAt, org?.timezone ?? "America/Lima")
                            : L("ADM_PPL_NEVER"),
                        ].join(" · ")}
                      </span>
                    </span>
                    <span className={ui.rowEnd} aria-hidden="true">
                      <ChatIcon name="chevron-right" />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <div className={styles.tableWrap}>
              <DataTable
                label={L("ADM_PPL_TAB_USERS")}
                rows={list.items}
                rowKey={(row) => row.userId}
                href={(row) => userHref(orgId, row.userId, row.status)}
                columns={[
                  {
                    key: "user",
                    title: L("ADM_PPL_COL_USER"),
                    render: (row) => (
                      <span className={styles.person}>
                        <Initials name={row.name} seed={row.userId} />
                        <span className={styles.personText}>
                          <strong>{row.name}</strong>
                          <span>{row.email}</span>
                        </span>
                      </span>
                    ),
                  },
                  {
                    key: "status",
                    title: L("ADM_PPL_COL_STATUS"),
                    render: (row) => <PersonStatusTag status={row.status} />,
                  },
                  { key: "role", title: L("ADM_PPL_COL_ROLE"), render: (row) => <RoleTag role={row.role} /> },
                  {
                    key: "last",
                    title: L("ADM_PPL_COL_LAST"),
                    render: (row) =>
                      row.lastLoginAt
                        ? whenIn(L, locale, row.lastLoginAt, org?.timezone ?? "America/Lima")
                        : L("ADM_PPL_NEVER"),
                  },
                  {
                    key: "modules",
                    title: L("ADM_PPL_COL_MODULES"),
                    // Propietario y Administrador entran a todos por su rol: lo mismo que dice su ficha › Módulos.
                    render: (row) =>
                      row.modulesByRole ? (
                        <span className={styles.fade}>{L("ADM_PPL_MODULES_ALL_BY_ROLE")}</span>
                      ) : row.modules.length ? (
                        <span className={styles.fade}>
                          {joinList(row.modules.map((code) => moduleName(L, code, moduleNames)))}
                        </span>
                      ) : (
                        "—"
                      ),
                  },
                  {
                    key: "inboxes",
                    title: L("ADM_PPL_COL_INBOXES"),
                    render: (row) =>
                      row.inboxes && row.inboxes.length ? (
                        <span className={styles.fade}>{joinList(row.inboxes.map((item) => item.name))}</span>
                      ) : (
                        "—"
                      ),
                  },
                  {
                    key: "menu",
                    title: "",
                    render: (row) => (
                      <RowMenu
                        label={formatText(L("ADM_PPL_ACTIONS_FOR"), row.name)}
                        title={row.name}
                        items={actions.items(
                          { userId: row.userId, name: row.name, status: row.status, isOwner: row.isOwner },
                          { withBlock: false, withUnlock: true },
                        )}
                      />
                    ),
                  },
                ]}
                mobile={(row) => ({ title: row.name, line: row.email })}
              />
            </div>
          )}
          <ListFooter list={list} />
        </>
      )}
      {actions.windows}
    </>
  );
}

type Invitation = {
  invitationId: number;
  email: string;
  role: string;
  status: string;
  sentAt: string;
  expiresAt: string;
};

const invitationStatus = (status: string) =>
  ({
    PENDING: "INV_STATUS_PENDING",
    ACCEPTED: "INV_STATUS_ACCEPTED",
    EXPIRED: "INV_STATUS_EXPIRED",
    REVOKED: "INV_STATUS_REVOKED",
  })[status] ?? "USAGE_UNKNOWN";

/**
 * `emp-invitaciones` · Personas › Invitaciones (§5.4): tabla paginada en Identity con estado (Pendiente en ocre), enviada
 * y vencimiento con la fecha completa en la zona de la empresa (D12). Solo las pendientes tienen ⋮: Reenviar y Revocar
 * (con confirmación).
 */
export function InvitationsPage({ orgId }: { orgId: number }) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const mobile = useIsMobile();
  const list = useServerList<Invitation>(`/api/admin/organizations/${orgId}/invitations`);
  const org = (list.extra?.organization as OrgHead | undefined) ?? null;
  const [revoke, setRevoke] = useState<Invitation | null>(null);
  useOpenOrg(org);
  useTitle(L("ADM_ORG_PEOPLE"));
  if (list.state === "notFound") return <OrgNotFound />;
  const head = <PeopleHead orgId={orgId} current="invites" action={<AddUser orgId={orgId} />} />;
  if (list.state === "forbidden")
    return <PageState head={head} title={L("ADM_FORBIDDEN")} line={L("ADM_FORBIDDEN_LINE")} />;
  const zone = org?.timezone ?? "America/Lima";
  const resend = async (item: Invitation) => {
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/invitations/${item.invitationId}/resend`,
      {},
    );
    if (reply.status === 200 && reply.body?.saved) {
      toast.show(formatText(L("ADM_PPL_INVITE_RESENT"), item.email));
      list.reload();
    } else toast.show(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
  };
  const menu = (item: Invitation) =>
    item.status === "PENDING" ? (
      <RowMenu
        label={formatText(L("ADM_PPL_ACTIONS_FOR"), item.email)}
        title={item.email}
        items={[
          { label: L("ADM_PPL_RESEND"), onSelect: () => void resend(item) },
          { label: L("ADM_PPL_REVOKE_INVITE_MENU"), danger: true, onSelect: () => setRevoke(item) },
        ]}
      />
    ) : null;
  // Vence: solo dice algo de una pendiente o una vencida (una aceptada o revocada ya no vence); sin el año si es este.
  const expires = (item: Invitation) =>
    item.status === "PENDING" || item.status === "EXPIRED"
      ? dateIn(locale, item.expiresAt, zone, new Date(item.expiresAt).getFullYear() !== new Date().getFullYear())
      : "—";
  const status = (item: Invitation) =>
    item.status === "PENDING" ? (
      <span className="tm-tag ochre">{L(invitationStatus(item.status))}</span>
    ) : (
      L(invitationStatus(item.status))
    );
  return (
    <>
      {head}
      {list.state === "loading" ? (
        <RowsSkeleton rows={5} table={!mobile} />
      ) : list.state === "error" ? (
        <Notice
          tone="error"
          action={
            <button type="button" className="tm-btn secondary" onClick={list.reload}>
              {L("ADM_RETRY")}
            </button>
          }
        >
          {L("ADM_UNAVAILABLE_LINE")}
        </Notice>
      ) : list.items.length === 0 ? (
        <div className="tm-empty" role="status" data-part="vacio">
          <strong>{L("ADM_PPL_NO_INVITES")}</strong>
          <a className="tm-btn primary" href={`/Organizations/AgregarUsuario?id=${orgId}`}>
            {L("ORG_ADD_USER")}
          </a>
        </div>
      ) : (
        <>
          <Counter>{plural(L, locale, list.total, "ADM_PPL_N_INVITES_ONE", "ADM_PPL_N_INVITES")}</Counter>
          {mobile ? (
            // C17 con el ⋮ en la fila: las acciones de una invitación no tienen otra pantalla.
            <ul className={`tm-list ${ui.list}`} aria-label={L("ADM_PPL_TAB_INVITES")} data-part="lista">
              {list.items.map((item) => (
                <li key={item.invitationId} className={ui.row}>
                  <span className={ui.rowText}>
                    <span className={ui.line1}>
                      <strong>{item.email}</strong>
                      {status(item)}
                    </span>
                    <span className={ui.line2}>
                      {L(roleKey(item.role))}
                      {expires(item) !== "—" ? ` · ${formatText(L("ADM_PPL_EXPIRES"), expires(item))}` : ""}
                    </span>
                  </span>
                  {menu(item) ? <span className={ui.rowEnd}>{menu(item)}</span> : null}
                </li>
              ))}
            </ul>
          ) : (
            <DataTable
              label={L("ADM_PPL_TAB_INVITES")}
              rows={list.items}
              rowKey={(row) => row.invitationId}
              columns={[
                { key: "email", title: L("ADM_PPL_COL_EMAIL"), render: (row) => <strong>{row.email}</strong> },
                { key: "role", title: L("ADM_PPL_COL_ROLE"), render: (row) => L(roleKey(row.role)) },
                { key: "status", title: L("ADM_PPL_COL_STATUS"), render: status },
                { key: "sent", title: L("ADM_PPL_COL_SENT"), render: (row) => whenIn(L, locale, row.sentAt, zone) },
                {
                  key: "expires",
                  title: L("ADM_PPL_COL_EXPIRES"),
                  render: (row) => expires(row),
                },
                { key: "menu", title: "", render: (row) => menu(row) },
              ]}
              mobile={(row) => ({ title: row.email, line: L(roleKey(row.role)) })}
            />
          )}
          <ListFooter list={list} />
        </>
      )}
      <ConfirmWindow
        open={!!revoke}
        title={L("ADM_PPL_REVOKE_INVITE_TITLE")}
        line={formatText(L("ADM_PPL_REVOKE_INVITE_LINE"), revoke?.email ?? "")}
        action={L("ADM_PPL_REVOKE_INVITE")}
        onClose={() => setRevoke(null)}
        onConfirm={async () => {
          const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
            `/api/admin/organizations/${orgId}/invitations/${revoke!.invitationId}/revoke`,
            {},
          );
          if (reply.status !== 200 || !reply.body?.saved) {
            toast.show(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
            return false;
          }
          toast.show(L("ADM_PPL_INVITE_REVOKED"));
          list.reload();
          return true;
        }}
      />
    </>
  );
}

type OwnerData = {
  organization: OrgHead;
  owner: { userId: number; displayName: string; email: string; ownerSince: string | null } | null;
  /** Personas activas que pueden recibir la propiedad (sin el propietario); se buscan al elegir (AR15). */
  candidatesTotal: number;
  maskedEmail: string | null;
};

type Candidate = { userId: number; name: string; email: string; role: string };

/**
 * `emp-propietario` · Personas › Propietario (§5.4): el propietario actual y desde cuándo; «Transferir propiedad» en dos
 * pasos (Persona · Código) y «Recuperar propiedad» con la persona y un motivo (queda en Auditoría). Sin propietario, un
 * aviso de advertencia con «Recuperar propiedad».
 */
export function OwnerPage({ orgId }: { orgId: number }) {
  const { L, locale } = useTexts();
  const data = useAiData<OwnerData>(`/api/admin/organizations/${orgId}/owner`);
  const [dialog, setDialog] = useState<"transfer" | "recover" | null>(null);
  useOpenOrg(data.data?.organization ?? null);
  useTitle(L("ADM_ORG_PEOPLE"));
  if (data.state === "missing") return <OrgNotFound />;
  const owner = data.data?.owner ?? null;
  const head = (
    <PeopleHead
      orgId={orgId}
      current="owner"
      action={
        owner ? (
          <button type="button" className={`tm-btn primary ${ui.desktopInline}`} onClick={() => setDialog("transfer")}>
            {L("ADM_PPL_TRANSFER")}
          </button>
        ) : null
      }
    />
  );
  if (data.state === "failed" && !data.data)
    return (
      <PageState
        head={head}
        title={data.error?.status === 403 ? L("ADM_FORBIDDEN") : L("ADM_UNAVAILABLE")}
        line={data.error?.status === 403 ? L("ADM_FORBIDDEN_LINE") : L("ADM_UNAVAILABLE_LINE")}
        action={
          data.error?.status === 403 ? undefined : (
            <button type="button" className="tm-btn secondary" onClick={data.retry}>
              {L("ADM_RETRY")}
            </button>
          )
        }
      />
    );
  if (!data.data)
    return (
      <>
        {head}
        <RowsSkeleton rows={1} />
      </>
    );
  const zone = data.data.organization.timezone;
  return (
    <>
      {head}
      {owner ? (
        <>
          <div className={styles.ownerRow} data-part="propietario">
            <Initials name={owner.displayName} seed={owner.userId} large />
            <span className={styles.ownerText}>
              <span className={styles.ownerName}>
                {owner.displayName}
                <span className="tm-tag accent">{L("ADM_PPL_ROLE_OWNER")}</span>
              </span>
              <span className={styles.ownerLine}>
                {owner.email}
                {owner.ownerSince
                  ? ` · ${formatText(L("ADM_PPL_OWNER_SINCE"), dateIn(locale, owner.ownerSince, zone))}`
                  : ""}
              </span>
            </span>
          </div>
          <div className={styles.noticeAction} role="status">
            <span>
              <ChatIcon name="info" />
              {formatText(L("ADM_PPL_RECOVER_HINT"), owner.displayName)}
            </span>
            <button type="button" className={styles.noticeButton} onClick={() => setDialog("recover")}>
              {L("ADM_PPL_RECOVER")}
            </button>
          </div>
        </>
      ) : (
        <div className={`${styles.noticeAction} ${styles.noticeWarn}`} role="status">
          <span>
            <ChatIcon name="circle-alert" />
            {L("ADM_PPL_NO_OWNER")}
          </span>
          <button type="button" className={styles.noticeButton} onClick={() => setDialog("recover")}>
            {L("ADM_PPL_RECOVER")}
          </button>
        </div>
      )}
      {owner ? (
        <MobileActions>
          <button type="button" className="tm-btn primary" onClick={() => setDialog("transfer")}>
            {L("ADM_PPL_TRANSFER")}
          </button>
        </MobileActions>
      ) : null}
      <TransferWindow
        orgId={orgId}
        open={dialog === "transfer"}
        data={data.data}
        onClose={() => setDialog(null)}
        onDone={() => void data.reload()}
      />
      <RecoverWindow
        orgId={orgId}
        open={dialog === "recover"}
        data={data.data}
        onClose={() => setDialog(null)}
        onDone={() => void data.reload()}
      />
    </>
  );
}

/**
 * R8 · AR15: los candidatos a recibir la propiedad se buscan en Identity (activos, sin el propietario) 50 ms después de
 * la última tecla, de 20 en 20; nunca «las primeras 100». Solo se pide con la ventana abierta.
 */
function useCandidates(orgId: number, open: boolean, data: OwnerData) {
  const exclude = data.owner ? `&exclude=${data.owner.userId}` : "";
  return useServerOptions<Candidate>((search) =>
    open
      ? `/api/admin/organizations/${orgId}/owner/candidates?pageSize=20${exclude}${search ? `&search=${encodeURIComponent(search)}` : ""}`
      : null,
  );
}

const candidateOptions = (L: (key: string) => string, items: Candidate[]) =>
  items.map((item) => ({ value: String(item.userId), label: `${item.name} · ${L(roleKey(item.role))}` }));

const menuIcons = {
  chevron: <ChatIcon name="chevron-down" />,
  check: <ChatIcon name="check" />,
  searchIcon: <ChatIcon name="search" />,
  clearIcon: <ChatIcon name="x" />,
};

/** `transferir` · dos pasos a la vista: la persona y tu contraseña; después, el código que llega a tu correo. */
function TransferWindow({
  orgId,
  open,
  data,
  onClose,
  onDone,
}: {
  orgId: number;
  open: boolean;
  data: OwnerData;
  onClose: () => void;
  onDone: () => void;
}) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const people = useCandidates(orgId, open, data);
  const [step, setStep] = useState(0);
  const [target, setTarget] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [general, setGeneral] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setStep(0);
      setTarget("");
      setName("");
      setPassword("");
      setCode("");
      setDevCode(null);
      setErrors({});
      setGeneral(null);
    }
  }
  const send = async (body: object) => {
    setBusy(true);
    const reply = await postJson<{
      saved?: boolean;
      done?: boolean;
      devCode?: string | null;
      field?: string;
      messageKey?: string;
    }>(`/api/admin/organizations/${orgId}/owner-transfer`, body);
    setBusy(false);
    return reply;
  };
  const next = async () => {
    const found: Record<string, string> = {};
    if (!target) found.target = L("ADM_PPL_PERSON_REQUIRED");
    if (!password) found.password = L("ADM_PPL_PASSWORD_REQUIRED");
    setErrors(found);
    setGeneral(null);
    if (Object.keys(found).length) return;
    const reply = await send({ step: "start", targetUserId: Number(target), password });
    // La contraseña no queda en memoria del formulario después de usarla.
    setPassword("");
    if (reply.status === 200 && reply.body?.saved) {
      if (reply.body.done) return finish();
      setDevCode(reply.body.devCode ?? null);
      setStep(1);
      return;
    }
    if (reply.body?.field === "password") setErrors({ password: L(reply.body.messageKey ?? "ADM_PPL_PASSWORD_WRONG") });
    else setGeneral(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
  };
  const finish = () => {
    onClose();
    onDone();
    toast.show(formatText(L("ADM_PPL_TRANSFERRED"), name));
  };
  const confirm = async () => {
    if (!code.trim()) return setErrors({ code: L("ADM_PPL_CODE_REQUIRED") });
    const reply = await send({ step: "confirm", targetUserId: Number(target), code: code.trim() });
    if (reply.status === 200 && reply.body?.saved) return finish();
    if (reply.body?.field === "code") setErrors({ code: L(reply.body.messageKey ?? "ADM_PPL_CODE_WRONG") });
    else setGeneral(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
  };
  return (
    <Window
      open={open}
      wide
      title={L("ADM_PPL_TRANSFER")}
      onClose={onClose}
      steps={{ labels: [L("ADM_PPL_PERSON"), L("ADM_PPL_CODE")], current: step }}
      footer={
        step === 0 ? (
          <>
            <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
              {L("ACTION_CANCEL")}
            </button>
            <button
              type="button"
              className="tm-btn primary"
              disabled={busy}
              aria-busy={busy || undefined}
              onClick={() => void next()}
            >
              {L("ADM_PPL_CONTINUE")}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="tm-btn secondary" onClick={() => setStep(0)} disabled={busy}>
              {L("ADM_PPL_BACK")}
            </button>
            <button
              type="button"
              className="tm-btn primary"
              disabled={busy}
              aria-busy={busy || undefined}
              onClick={() => void confirm()}
            >
              {L("ADM_PPL_TRANSFER")}
            </button>
          </>
        )
      }
    >
      <div className={ui.windowBlock}>
        {general ? <Notice tone="error">{general}</Notice> : null}
        {step === 0 ? (
          <>
            <p className={styles.windowText}>{L("ADM_PPL_TRANSFER_LINE")}</p>
            {data.candidatesTotal === 0 ? (
              <Notice tone="warning">{L("ADM_PPL_NO_CANDIDATES")}</Notice>
            ) : (
              <div className={`tm-field${errors.target ? " invalid" : ""}`}>
                <label htmlFor="transfer-person">{L("ADM_PPL_NEW_OWNER")}</label>
                <ChoiceMenu
                  id="transfer-person"
                  label={L("ADM_PPL_NEW_OWNER")}
                  value={target}
                  placeholder={L("ADM_PPL_CHOOSE_ACTIVE")}
                  options={candidateOptions(L, people.items)}
                  onSearch={people.search}
                  note={optionsNote(L, locale, people)}
                  currentLabel={name || undefined}
                  onChange={(value) => {
                    setTarget(value);
                    setName(people.items.find((item) => String(item.userId) === value)?.name ?? "");
                    setErrors((now) => ({ ...now, target: "" }));
                  }}
                  invalid={!!errors.target}
                  searchLabel={L("CFG1_SEARCH")}
                  clearSearchLabel={L("CFG1_R1_CLEAR_SEARCH")}
                  {...menuIcons}
                />
                {errors.target ? <span className="tm-field-error">{errors.target}</span> : null}
              </div>
            )}
            <PasswordField
              id="transfer-password"
              label={L("ADM_PPL_YOUR_PASSWORD")}
              value={password}
              autoComplete="current-password"
              error={errors.password}
              help={L("ADM_PPL_YOUR_PASSWORD_HELP")}
              onChange={(value) => (setPassword(value), value && setErrors((now) => ({ ...now, password: "" })))}
            />
          </>
        ) : (
          <>
            <p className={styles.windowText}>
              {data.maskedEmail ? formatText(L("ADM_PPL_CODE_SENT"), data.maskedEmail) : L("ADM_PPL_CODE_SENT_NOMAIL")}
            </p>
            {devCode ? <Notice tone="info">{formatText(L("ADM_PPL_DEV_CODE"), devCode)}</Notice> : null}
            <TextField
              id="transfer-code"
              label={L("ADM_PPL_CODE")}
              value={code}
              maxLength={20}
              autoComplete="one-time-code"
              placeholder={L("ADM_PPL_CODE_PLACEHOLDER")}
              error={errors.code}
              onChange={(value) => (setCode(value), value.trim() && setErrors({}))}
            />
          </>
        )}
      </div>
    </Window>
  );
}

/** `recuperar-prop` · la persona activa que recibe la propiedad y el motivo (hasta 200); queda registrado en Auditoría. */
function RecoverWindow({
  orgId,
  open,
  data,
  onClose,
  onDone,
}: {
  orgId: number;
  open: boolean;
  data: OwnerData;
  onClose: () => void;
  onDone: () => void;
}) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const people = useCandidates(orgId, open, data);
  const [target, setTarget] = useState("");
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [general, setGeneral] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setTarget("");
      setName("");
      setReason("");
      setErrors({});
      setGeneral(null);
    }
  }
  const submit = async () => {
    const found: Record<string, string> = {};
    if (!target) found.target = L("ADM_PPL_PERSON_REQUIRED");
    if (!reason.trim()) found.reason = L("ORGCORE_REASON_REQUIRED");
    setErrors(found);
    setGeneral(null);
    if (Object.keys(found).length) return;
    setBusy(true);
    const reply = await postJson<{ saved?: boolean; field?: string; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/owner-transfer/recover`,
      { newOwnerUserId: Number(target), reason: reason.trim() },
    );
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved) {
      onClose();
      onDone();
      toast.show(L("ADM_PPL_RECOVERED"));
      return;
    }
    setGeneral(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
  };
  return (
    <Window
      open={open}
      title={L("ADM_PPL_RECOVER")}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className="tm-btn primary"
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={() => void submit()}
          >
            {L("ADM_PPL_RECOVER_ACTION")}
          </button>
        </>
      }
    >
      <div className={ui.windowBlock}>
        <p className={styles.windowText}>
          {data.owner ? formatText(L("ADM_PPL_RECOVER_LINE"), data.owner.displayName) : L("ADM_PPL_RECOVER_LINE_NONE")}
        </p>
        {general ? <Notice tone="error">{general}</Notice> : null}
        <div className={`tm-field${errors.target ? " invalid" : ""}`}>
          <label htmlFor="recover-person">{L("ADM_PPL_NEW_OWNER")}</label>
          <ChoiceMenu
            id="recover-person"
            label={L("ADM_PPL_NEW_OWNER")}
            value={target}
            placeholder={L("ADM_PPL_CHOOSE_ACTIVE")}
            options={candidateOptions(L, people.items)}
            onSearch={people.search}
            note={optionsNote(L, locale, people)}
            currentLabel={name || undefined}
            onChange={(value) => {
              setTarget(value);
              setName(people.items.find((item) => String(item.userId) === value)?.name ?? "");
              setErrors((now) => ({ ...now, target: "" }));
            }}
            invalid={!!errors.target}
            searchLabel={L("CFG1_SEARCH")}
            clearSearchLabel={L("CFG1_R1_CLEAR_SEARCH")}
            {...menuIcons}
          />
          {errors.target ? <span className="tm-field-error">{errors.target}</span> : null}
        </div>
        <div className={`tm-field${errors.reason ? " invalid" : ""}`}>
          <label htmlFor="recover-reason">{L("ADM_PPL_REASON")}</label>
          <textarea
            id="recover-reason"
            className={styles.textarea}
            value={reason}
            maxLength={200}
            placeholder={L("ADM_PPL_RECOVER_EXAMPLE")}
            aria-invalid={errors.reason ? "true" : undefined}
            aria-describedby="recover-reason-help"
            onChange={(event) => {
              setReason(event.target.value);
              if (event.target.value.trim()) setErrors((now) => ({ ...now, reason: "" }));
            }}
          />
          {errors.reason ? <span className="tm-field-error">{errors.reason}</span> : null}
          <small id="recover-reason-help" className="tm-field-help">
            {L("ADM_PPL_REASON_200")}
          </small>
        </div>
      </div>
    </Window>
  );
}
